import cx from 'classnames';
import { useContext, useMemo, useState } from 'react';
import { CtaWrapper, useGetCtaWrapperContent } from '@/vdp/CtaWrapper';
import {
  StickyWrapper,
  VehicleInfoSidebar
} from '@/vdp/VehicleDetailsContainer/styles';
import VehicleInfo, { MainCta } from '@/vdp/VehicleInfo';
import Section, { SectionName } from '~/constants/vdp/section';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useIsDealershipPage } from '~/context/dealership/StaticContext';
import GlobalContext from '~/context/vdp/GlobalContext';
import { SectionAnalyticsProvider } from '~/context/vdp/SectionAnalyticsContext';
import useShowOutOfAreaError from '~/hooks/vdp/selectors/useShowOutOfAreaError';
import useShouldRenderCondensedHero from '~/hooks/vdp/useShouldRenderCondensedHero';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import { Banners, useGetShowBanners } from './Banners';
import DesktopActions from './DesktopActions';
import FallbackVehicleInfoCard from './FallbackVehicleInfoCard';
import OutOfAreaErrorContent from './OutOfAreaErrorContent';
import Sentinel from './VehicleInfoSentinel';

function getContentPaddingClasses(
  isBannerShowing: boolean,
  showOutOfAreaError: boolean,
  showCtaFooter: boolean
): string {
  const topPadding = isBannerShowing
    ? 'sm:pt-2xl lg:pt-3xl'
    : 'sm:pt-3xl lg:pt-4xl';

  const bottomPadding = showOutOfAreaError
    ? ''
    : showCtaFooter
    ? 'pb-2xl sm:pb-2xl lg:pb-3xl'
    : 'pb-2xl sm:pb-xl lg:pb-2xl';

  return cx(
    'flex flex-col gap-2xl px-2xl py-xl sm:px-3xl lg:px-4xl',
    topPadding,
    bottomPadding
  );
}

function VehicleInfoCard() {
  const { isBannerShowing } = useGetShowBanners();
  const isDealership = useIsDealershipPage();
  const isMobile = useIsMobile();
  const { showCtaFooter } = useGetCtaWrapperContent();
  const showOutOfAreaError = useShowOutOfAreaError();
  const { isBreadcrumbScrolledOut } = useContext(GlobalContext);

  return (
    <div
      className="w-full bg-white sm:rounded-xl sm:shadow-[0px_28px_56px_0px_rgba(36,_41,_46,_0.04),_0px_8px_16px_0px_rgba(36,_41,_46,_0.12)]"
      data-testid="vehicle-info-card"
    >
      <Banners />
      <div
        className={getContentPaddingClasses(
          !!isBannerShowing,
          showOutOfAreaError,
          showCtaFooter
        )}
      >
        <VehicleInfo enablePriceObserver />
      </div>
      <CtaWrapper shouldShow={!isMobile || isDealership}>
        {showOutOfAreaError ? (
          <OutOfAreaErrorContent showDesktopActions={isBreadcrumbScrolledOut} />
        ) : (
          <MainCta />
        )}
      </CtaWrapper>
      {!isMobile && <DesktopActions showTopBorder={showOutOfAreaError} />}
    </div>
  );
}

function VehicleInfoCardWrapper() {
  const isMobile = useIsMobile();
  const renderCondensedHeader = useShouldRenderCondensedHero();
  const [isSticky, setIsSticky] = useState(false);

  const analyticsAttributes = useMemo(() => ({ isSticky }), [isSticky]);
  const hideVehicleInfoCard = renderCondensedHeader && isMobile;

  return hideVehicleInfoCard ? null : (
    <VehicleInfoSidebar isCondensedLayout={renderCondensedHeader}>
      <Sentinel setIsSticky={setIsSticky} />
      <SectionAnalyticsProvider
        analyticsId={SectionName.VehicleInfo}
        analyticsAttributes={analyticsAttributes}
      >
        <StickyWrapper>
          <VehicleInfoCard />
        </StickyWrapper>
      </SectionAnalyticsProvider>
    </VehicleInfoSidebar>
  );
}

export default withErrorBoundary(
  VehicleInfoCardWrapper,
  FallbackVehicleInfoCard,
  undefined,
  Section.VehicleInfo
);
